import type { Router } from '@inertiajs/core';
import { router as inertiaRouter } from '@inertiajs/react';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
import type React from 'react';
import { type FormEventHandler, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { FormTextInput } from '@/components/ui/form/FormTextInput';
import { cn } from '@/lib/utils';

import { type FieldErrors, firstErrorPerField } from './authFormErrors';
import { AuthStatusMessage } from './AuthStatusMessage';
import { inlineTwoFactorFields } from './inlineTwoFactorFields';

const RESET_LINK_SENT_MESSAGE =
  'If an account exists, we have sent a password reset link to your email address.';

export type ForgotPasswordFormProps = {
  /** The authenticatable resource whose password is being reset. */
  resourceId: string;

  /** Prefills the email field, e.g. with the address already identified on the login form. */
  initialIdentifier?: string;

  /** A server-flashed message to surface above the form. */
  statusMessage?: string | null;

  identifierLabel?: React.ReactNode;
  identifierPlaceholder?: string;
  submitLabel?: React.ReactNode;
  backLabel?: React.ReactNode;

  className?: string;
  buttonClassName?: string;

  /** Renders a back control that returns to the login form in place, without navigating. */
  onBack?: () => void;

  /** Renders a back control that navigates, for surfaces where login is a separate page. */
  backHref?: string;

  /**
   * Finish a two-factor sign-in after the reset on the served Page `currentPageId`
   * instead of the hosted challenge.
   */
  twoFactor?: 'inline';

  /**
   * Return to the served Page `currentPageId` after the reset signs the visitor in,
   * instead of the workspace's default destination.
   */
  returnTo?: 'current';

  /** The served Page id that an `inline` or `current` reset returns to. */
  currentPageId?: string | null;

  /** Sends the request. Defaults to Inertia's router; the page playground passes one that blocks it. */
  router?: AuthFormRouter;
};

export type AuthFormRouter = Pick<Router, 'post' | 'reload'>;

/**
 * Requests a password reset link for an authenticatable resource. Shared by the
 * Diagonal-hosted forgot password page, the login form's inline reset step, and
 * `Sdk.Components.ForgotPasswordForm`.
 *
 * Confirms from its own state rather than a flashed message, so it reads the same
 * on a surface that reloads (the dedicated page) and one that stays put (a workspace
 * page). The response is identical whether or not an account exists — treat a
 * success as "the request was accepted", never as "an account was found".
 */
export function ForgotPasswordForm({
  resourceId,
  initialIdentifier = '',
  statusMessage = null,
  identifierLabel = 'Email',
  identifierPlaceholder = 'Email',
  submitLabel = 'Send password reset link',
  backLabel = 'Back to login',
  className,
  buttonClassName,
  onBack,
  backHref,
  twoFactor,
  returnTo,
  currentPageId = null,
  router = inertiaRouter
}: ForgotPasswordFormProps) {
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [processing, setProcessing] = useState(false);
  const [sent, setSent] = useState(false);

  const submit: FormEventHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setErrors({});

    router.post(
      `/auth/resource/${resourceId}/forgot-password`,
      {
        identifier,
        ...(returnTo === 'current' && currentPageId ? { returnTo, currentPageId } : {}),
        ...inlineTwoFactorFields(twoFactor, currentPageId)
      },
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
        onError: (resetErrors) => setErrors(firstErrorPerField(resetErrors)),
        onSuccess: () => setSent(true)
      }
    );
  };

  const banner = sent ? RESET_LINK_SENT_MESSAGE : statusMessage;

  return (
    <div className={cn('grid gap-2', className)}>
      {banner ? <AuthStatusMessage className="mb-2">{banner}</AuthStatusMessage> : null}

      <form onSubmit={submit} className="grid gap-2">
        <FormTextInput
          type="email"
          label={identifierLabel}
          placeholder={identifierPlaceholder}
          autoComplete="email"
          value={identifier}
          onChange={setIdentifier}
          error={errors.identifier}
        />

        <Button
          type="submit"
          className={cn('mt-2 w-full', buttonClassName)}
          disabled={processing || !identifier}
        >
          {submitLabel}
        </Button>
      </form>

      {onBack ? (
        <div className="mt-2 flex justify-center">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center justify-center text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="mr-2 size-4" />
            {backLabel}
          </button>
        </div>
      ) : null}

      {!onBack && backHref ? (
        <div className="mt-2 flex justify-center">
          <a
            href={backHref}
            className="inline-flex items-center justify-center text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="mr-2 size-4" />
            {backLabel}
          </a>
        </div>
      ) : null}
    </div>
  );
}
